import React from 'react';

import { EVIDENCE_TYPES, pathwayLabel, teamMeta } from '../../constants';
import { formatDue, getPatient } from '../../helpers';
import AthenaLink from '../AthenaLink';
import { Badge } from '../Badge';
import styles from './TaskDetail.module.scss';

const TaskDetail = ({ task, onClose }) => {
  if (!task) return null;

  const patient = task?.patient ?? getPatient(task?.patientId);
  const team = teamMeta[task.team];
  const due = task?.due ? formatDue(task.due) : { text: '—', urgent: false };
  const evidenceType = EVIDENCE_TYPES[task?.evidence?.type];

  return (
    <div className={styles.root}>
      <div className={styles.sectionHeader}>
        <div className={styles.headerTop}>
          <div className={styles.title}>{task.title}</div>
          <button type="button" onClick={onClose} className={styles.closeButton}>
            ×
          </button>
        </div>
        {task.caption && <div className={styles.caption}>{task.caption}</div>}
        <div className={styles.badgesRow}>
          {task.status === 'blocked' ? (
            <Badge variant="critical">Blocked</Badge>
          ) : task.status === 'flagged' ? (
            <Badge variant="flagged">Flagged</Badge>
          ) : (
            <Badge>{task.status}</Badge>
          )}
          <Badge variant="outline">{pathwayLabel[task.pathway]}</Badge>
          <Badge variant="outline">{task.priority}</Badge>
          {task.mustHave2026 && <Badge variant="edge">2026 Target</Badge>}
        </div>
      </div>

      <div className={styles.sectionInfo}>
        <div className={styles.infoGrid}>
          {[
            ['Patient', `${patient?.name} · ${patient?.mrn}`],
            ['Assigned to', `${task.owner} (${task.role})`],
            ['Team', team ? `${team.icon} ${team.label}` : '—'],
            ['Due', `${task.due}  ${due.text}`],
            ['Clinician', patient?.clinician],
          ].map(([key, value], index) => (
            <div key={index} className={styles.infoRow}>
              <span className={styles.infoLabel}>{key}</span>
              <span className={styles.infoValue}>{value}</span>
            </div>
          ))}
        </div>
        {patient && (
          <div className={styles.athenaLinkWrap}>
            <AthenaLink patient={patient} />
          </div>
        )}
        {task.noteUrl && (
          <a
            href={task.noteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.noteLink}
          >
            <div className={styles.noteBadge}>
              <span className={styles.noteBadgeText}>N</span>
            </div>
            <div className={styles.noteMeta}>
              <span className={styles.noteTitle}>View Signed Visit Note</span>
              <span className={styles.noteSub}>99205 · {patient?.name}</span>
            </div>
            <span className={styles.noteArrow}>↗</span>
          </a>
        )}
      </div>

      <div className={styles.sectionEvidence}>
        <div className={styles.sectionLabel}>Why this task exists</div>
        <div className={styles.evidenceCard}>
          <div className={styles.evidenceHeader}>
            <span className={styles.evidenceIcon}>{evidenceType?.icon}</span>
            <span className={styles.evidenceLabel}>{evidenceType?.label || 'Evidence'}</span>
            <span className={styles.evidenceSpacer} />
            <span className={styles.evidenceRef}>{task?.evidence?.sourceRef || '—'}</span>
          </div>
          <p className={styles.evidenceSummary}>{task?.evidence?.summary || '—'}</p>
          <div className={styles.evidenceSource}>Source: {task?.evidence?.source || '—'}</div>
        </div>
      </div>

      <div className={styles.sectionActions}>
        <button type="button" className={styles.primaryAction}>
          Mark Complete
        </button>
        <button type="button" className={styles.secondaryAction}>
          Reassign
        </button>
      </div>
    </div>
  );
};

export default TaskDetail;
